import { processMarkdown as baseProcessMarkdown, extractTableOfContents } from './markdown';

interface LanguageVariant {
	bash?: string;
	python?: string;
	nodejs?: string;
}

interface CodeBlockVariant {
	macos?: LanguageVariant;
	windows?: LanguageVariant;
	linux?: LanguageVariant;
	languages: string[];
	hasMultipleLanguages: boolean;
}

/**
 * Enhanced markdown processor with OS and Language aware code blocks
 */
// Simple hash function for deterministic IDs
function simpleHash(str: string): number {
	let hash = 0;
	for (let i = 0; i < str.length; i++) {
		const char = str.charCodeAt(i);
		hash = ((hash << 5) - hash) + char;
		hash = hash & hash; // Convert to 32bit integer
	}
	return Math.abs(hash);
}

export function processMarkdownWithOS(content: string): string {
	const codeBlocks: Map<string, string> = new Map();
	
	// First, extract and replace OS+Language-aware code blocks with placeholders
	let processed = content.replace(
		/```(\w+):os=([^\n]+)(?::lang=([^\n]+))?\n([\s\S]*?)```/g,
		(match, baseLang, osList, langList, code) => {
			const variants: CodeBlockVariant = {
				languages: [],
				hasMultipleLanguages: false
			};
			
			// Parse supported languages
			if (langList) {
				variants.languages = langList.split(',').map((s: string) => s.trim().toLowerCase());
				variants.hasMultipleLanguages = variants.languages.length > 1;
			} else {
				variants.languages = [baseLang.toLowerCase()];
				variants.hasMultipleLanguages = false;
			}
			
			// Parse OS and Language sections
			const osRegex = /^###OS:(\w+)$/gm;
			const langRegex = /^###LANG:(\w+)$/gm;
			
			// Split by OS sections first
			const osSections = code.split(osRegex);
			
			if (osSections.length > 1) {
				// Multi-OS block with sections
				for (let i = 1; i < osSections.length; i += 2) {
					const os = osSections[i].toLowerCase() as 'macos' | 'windows' | 'linux';
					const osContent = osSections[i + 1];
					
					if (os === 'macos' || os === 'windows' || os === 'linux') {
						variants[os] = {};
						
						// Check if this OS section has language subdivisions
						const langSections = osContent.split(langRegex);
						
						if (langSections.length > 1) {
							// Multiple languages within this OS
							for (let j = 1; j < langSections.length; j += 2) {
								const lang = langSections[j].toLowerCase() as 'bash' | 'python' | 'nodejs';
								const langCode = langSections[j + 1].trim();
								
								if (lang === 'bash' || lang === 'python' || lang === 'nodejs') {
									variants[os]![lang] = langCode;
								}
							}
						} else {
							// Single language for this OS (use base language)
							const defaultLang = variants.languages[0] as 'bash' | 'python' | 'nodejs';
							variants[os]![defaultLang] = osContent.trim();
						}
					}
				}
			} else {
				// Single code for all specified OS
				const osArray = osList.split(',').map((s: string) => s.trim().toLowerCase());
				const codeContent = code.trim();
				
				osArray.forEach((os: string) => {
					if (os === 'macos' || os === 'windows' || os === 'linux') {
						variants[os] = {};
						const defaultLang = variants.languages[0] as 'bash' | 'python' | 'nodejs';
						variants[os]![defaultLang] = codeContent;
					}
				});
			}
			
			// Generate deterministic block ID based on content
			const blockId = simpleHash(match);
			
			// Generate HTML for enhanced code block
			const html = generateEnhancedCodeBlockHTML(variants, blockId);
			const placeholder = `<!--ENHANCED_CODE_BLOCK_${blockId}-->`;
			codeBlocks.set(placeholder, html);
			return placeholder;
		}
	);
	
	// Process the rest with the base markdown processor
	processed = baseProcessMarkdown(processed);
	
	// Replace placeholders with actual code block HTML
	codeBlocks.forEach((html, placeholder) => {
		processed = processed.replace(placeholder, html);
	});
	
	return processed;
}

function generateEnhancedCodeBlockHTML(variants: CodeBlockVariant, id: number): string {
	const availableOS = Object.keys(variants).filter(k => k !== 'languages' && k !== 'hasMultipleLanguages') as ('macos' | 'windows' | 'linux')[];
	
	// Generate the HTML structure
	let html = `<div class="enhanced-code-block" data-block-id="enhanced-block-${id}" data-has-multiple-lang="${variants.hasMultipleLanguages}">`;
	
	// Header with language selector (if multiple languages) and copy button
	html += `<div class="enhanced-code-header">`;
	
	// Language selector (only if multiple languages)
	if (variants.hasMultipleLanguages) {
		html += `<div class="enhanced-code-lang-tabs">`;
		variants.languages.forEach((lang, index) => {
			const langName = lang === 'bash' ? 'Bash' : lang === 'python' ? 'Python' : 'Node.js';
			// Don't add active class here - let JavaScript handle it
			html += `
				<button class="enhanced-code-lang-tab" data-lang="${lang}" aria-label="Show ${langName} version">
					<span class="lang-tab-icon">${getLangIcon(lang)}</span>
					<span class="lang-tab-label">${langName}</span>
				</button>`;
		});
		html += `</div>`;
	}
	
	// Copy button
	html += `
		<button class="enhanced-code-copy" aria-label="Copy code">
			<svg class="copy-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
				<rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
				<path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
			</svg>
			<span class="copy-text">Copy</span>
		</button>`;
	
	html += `</div>`;
	
	// Code content containers - all start hidden, inline script will show the right one
	availableOS.forEach(os => {
		if (variants[os]) {
			variants.languages.forEach(lang => {
				if (variants[os]![lang as 'bash' | 'python' | 'nodejs']) {
					const code = variants[os]![lang as 'bash' | 'python' | 'nodejs'];
					// All hidden by default - prevents any flash
					html += `<div class="enhanced-code-content" data-os="${os}" data-lang="${lang}" style="display: none;">${formatCodeWithLineNumbers(code!, lang)}</div>`;
				}
			});
		}
	});
	
	html += `</div>`;
	
	return html;
}

function formatCodeWithLineNumbers(code: string, language: string): string {
	// Escape HTML
	const escaped = code
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;');
	
	// Split into lines and add line numbers
	const lines = escaped.split('\n');
	const maxLineNumber = lines.length;
	const lineNumberWidth = String(maxLineNumber).length;
	
	let numberedLines = lines.map((line, index) => {
		const lineNumber = (index + 1).toString().padStart(lineNumberWidth, ' ');
		return `<span class="code-line"><span class="line-number">${lineNumber}</span><span class="line-content">${line}</span></span>`;
	}).join('\n');
	
	return `<pre><code class="language-${language}">${numberedLines}</code></pre>`;
}

function getOSIcon(os: string): string {
	switch (os) {
		case 'macos':
			return `<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
				<path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
			</svg>`;
		case 'windows':
			return `<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
				<path d="M3 12V6.75l6-1.32v6.48L3 12m17-9v8.75l-10.5-0.08V5.05L20 3M20 13l.08 8.75L9.5 20.84v-6.59L20 13M3 12.25v5.5l6 1.32v-6.48L3 12.25z"/>
			</svg>`;
		case 'linux':
			return `<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
				<path d="M12.504 0c-.155 0-.315.008-.48.021-4.226.333-3.105 4.807-3.17 6.298-.076 1.092-.3 1.953-1.05 3.02-.885 1.051-2.127 2.75-2.716 4.521-.278.832-.41 1.684-.287 2.489a.424.424 0 00-.11.135c-.26.268-.45.6-.663.839-.199.199-.485.267-.797.4-.313.136-.658.269-.864.68-.09.189-.136.394-.132.602 0 .199.027.4.055.536.058.399.116.728.04.97-.249.68-.28 1.145-.106.998.174-.146.4-.583.633-.611.206-.024.487.09.637.133.138.014.28.049.418.096.14.027.284.070.421.054.206-.024.487.09.637.133.138.014.28.049.418.096.14.027.284.070.421.054.263-.045.537-.074.749-.189.297-.21.74-.339.905-.777.116-.309.206-.664.287-.86.136-.319.475-.627.687-.735.199-.101.408-.174.619-.190.263-.045.537-.074.749-.189.297-.21.74-.339.905-.777.116-.309.206-.664.287-.86.136-.319.475-.627.687-.735.199-.101.408-.174.619-.190.234-.022.479-.010.684.043.234.067.442.199.637.281.263.120.537.309.749.440.297.210.74.339.905.777.116.309.206.664.287.860.136.319.475.627.687.735.199.101.408.174.619.190.234.022.479.010.684-.043.234-.067.442-.199.637-.281.263-.120.537-.309.749-.440.297-.210.74-.339.905-.777.116-.309.206-.664.287-.860.136-.319.475-.627.687-.735.199-.101.408-.174.619-.190.515-.035 1.036.069 1.382.342.263.210.352.693.234 1.049-.082.249-.274.443-.497.590-.199.129-.436.199-.684.199-.515 0-1.036-.066-1.382-.34-.263-.21-.352-.693-.234-1.049.082-.249.274-.443.497-.590.199-.129.436-.199.684-.199z"/>
			</svg>`;
		default:
			return '';
	}
}

function getLangIcon(lang: string): string {
	switch (lang) {
		case 'bash':
			return `<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
				<path d="M21.038 4.9l-7.577-4.498c-.8-.475-1.777-.475-2.577 0L3.307 4.9c-.8.475-1.307 1.267-1.307 2.2v9.8c0 .933.507 1.725 1.307 2.2l7.577 4.498c.8.475 1.777.475 2.577 0l7.577-4.498c.8-.475 1.307-1.267 1.307-2.2V7.1c0-.933-.507-1.725-1.307-2.2zM7.5 15.5l-2-2 2-2 1 1-1 1 1 1-1 1zm9 0l-1-1 1-1-1-1 1-1 2 2-2 2z"/>
			</svg>`;
		case 'python':
			return `<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
				<path d="M9.585 11.692h4.328s2.432.039 2.432-2.35V5.391S16.714 3 11.936 3C7.362 3 7.647 4.983 7.647 4.983l.006 2.055h4.363v.617H5.92s-2.927-.332-2.927 4.282 2.555 4.45 2.555 4.45h1.524v-2.141s-.083-2.554 2.513-2.554zm-.056-5.74a.784.784 0 11-.784-.783.784.784 0 01.784.783z"/>
				<path d="M18.452 7.532v2.141s.083 2.554-2.513 2.554h-4.328s-2.432-.04-2.432 2.35v3.951s-.369 2.391 4.409 2.391 4.768-1.983 4.768-1.983l-.006-2.055h-4.363v-.617h6.097s2.927.332 2.927-4.282-2.555-4.45-2.555-4.45h-1.524zm-.056 9.26a.784.784 0 11.784.783.784.784 0 01-.784-.783z"/>
			</svg>`;
		case 'nodejs':
			return `<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
				<path d="M12 1.85c-.27 0-.55.07-.78.2l-7.44 4.3c-.48.28-.78.8-.78 1.36v8.58c0 .56.3 1.08.78 1.36l7.44 4.3c.48.28 1.08.28 1.56 0l7.44-4.3c.48-.28.78-.8.78-1.36V7.71c0-.56-.3-1.08-.78-1.36l-7.44-4.3c-.23-.13-.51-.2-.78-.2zm-.45 15.11c0 .07-.05.12-.12.12-.93-.01-1.85-.49-2.4-1.21v.01c-.5-.63-.63-1.45-.31-2.26.25-.64.73-1.18 1.37-1.46 1.03-.43 2.22-.16 2.96.67.43.48.63 1.11.55 1.73-.05.39-.18.75-.4 1.06-.25.35-.57.63-.96.8-.17.07-.35.12-.53.14-.05.01-.11.02-.16.01zm.45-2.92c-.48 0-.87.39-.87.87s.39.87.87.87.87-.39.87-.87-.39-.87-.87-.87z"/>
			</svg>`;
		default:
			return '';
	}
}

/**
 * Extract table of contents with enhanced code blocks handled
 */
export function extractTableOfContentsWithOS(content: string): any[] {
	// Remove enhanced code blocks before extracting TOC
	const cleanContent = content.replace(
		/```(\w+):os=([^\n]+)(?::lang=([^\n]+))?\n([\s\S]*?)```/g,
		'```$1\n$4```'
	);
	
	return extractTableOfContents(cleanContent);
}